import React from 'react';
import { useSelector } from 'react-redux';

import { ReactComponent as ChevronIcon } from 'images/chevron.svg';
import { AvailableShift } from 'components/FinancialCheckup/FinancialDashboard/AvailableShiftsSection';
import HighlightBadge from 'components/HighlightBadge';
import { useNavigate } from 'hooks/useNavigate';
import { RoutePath } from 'enums/Routes';
import { formatShiftDateLabel } from 'utils/shiftDateUtils';
import { getShiftBadgeText } from 'utils/shiftGoalBadgeUtils';
import { getLoanGoal } from 'selectors/getLoanGoal';
import { AICoachGoalPriority, AICoachGoalType } from 'enums/AICoach';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';

import styles from './ShiftCard.module.scss';

interface ShiftCardProps {
  shift: AvailableShift;
}

const SHIFT_CLICKED_EVENT = 'shift_clicked';

const ShiftCard: React.FC<ShiftCardProps> = ({ shift }) => {
  const navigate = useNavigate();
  const { aiCoachGoals } = useSelector(getLoanGoal);
  const { date, id, timeRange, goalAcceleration, amount } = shift;
  const primaryGoal = aiCoachGoals?.find((goal) => goal.priority === AICoachGoalPriority.Primary);
  const goalType = primaryGoal?.type ?? AICoachGoalType.BuildEmergencyFund;
  const badgeText = getShiftBadgeText(goalType, goalAcceleration, primaryGoal?.description);

  const handleClick = () => {
    analytics.track(SHIFT_CLICKED_EVENT, { shift_id: id });
    navigate(RoutePath.ShiftDetails.replace(':id', id) as RoutePath);
  };

  return (
    <div className={styles.shiftCard} onClick={handleClick}>
      <p className={styles.date}>{formatShiftDateLabel(date)}</p>
      {badgeText ? <HighlightBadge customText={badgeText} /> : null}
      <div className={styles.column}>
        <div className={styles.row}>
          <p className={styles.timeRange}>{timeRange}</p>
          {amount ? <p className={styles.payment}>{formatMonetaryAmount(amount, true)}</p> : null}
          <div className={styles.chevronContainer}>
            <ChevronIcon className={styles.chevron} />
          </div>
        </div>
        <div className={styles.row}>
          <p className={styles.locationName}>{shift.location}</p>
        </div>
      </div>
    </div>
  );
};

export default ShiftCard;
